// Select コンポーネント - UI/UX設計仕様書準拠

import React from 'react';
import {
  FormControl,
  FormLabel,
  Select as MuiSelect,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  SelectProps as MuiSelectProps,
  useTheme,
} from '@mui/material';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<MuiSelectProps, 'onChange' | 'error'> {
  label?: string;
  options: SelectOption[];
  onChange?: (value: string) => void;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  required?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      onChange,
      error,
      helperText,
      fullWidth = true,
      required,
      value,
      placeholder,
      sx,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();

    const handleChange = (event: SelectChangeEvent<unknown>) => {
      const newValue = event.target.value as string;
      onChange?.(newValue);
    };

    const hasError = Boolean(error);

    return (
      <FormControl fullWidth={fullWidth} error={hasError}>
        {label && (
          <FormLabel
            required={required}
            sx={{
              mb: 1,
              color: hasError ? 'error.main' : 'text.primary',
              fontWeight: 500,
              fontSize: '0.875rem',
              '&.Mui-focused': {
                color: hasError ? 'error.main' : 'primary.main',
              },
            }}
          >
            {label}
          </FormLabel>
        )}

        <MuiSelect
          {...(props as any)}
          ref={ref}
          value={value || ''}
          onChange={handleChange}
          displayEmpty={Boolean(placeholder)}
          sx={[
            {
              backgroundColor: 'background.paper',
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? 'error.main' : 'grey.300',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? 'error.main' : 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? 'error.main' : 'primary.main',
                borderWidth: 2,
              },
              '& .MuiSelect-select': {
                padding: '12px 14px',
                fontSize: '0.875rem',
                lineHeight: 1.4,
              },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...props}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              <span style={{ color: theme.palette.text.disabled }}>
                {placeholder}
              </span>
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>

        {(error || helperText) && (
          <FormHelperText sx={{ mt: 1, mx: 0 }}>
            {error || helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
);

Select.displayName = 'Select';

export default Select;
